import React from 'react';

const PrivacyPolicySection = () => (
  <section id="privacy-policy" className="relative z-10 -mt-16 md:-mt-24 pb-24 px-4">
    <div className="max-w-5xl mx-auto bg-white border border-[#f4e7da] shadow-[0_25px_65px_rgba(16,24,40,0.08)] rounded-[22px] overflow-hidden">
      <div className="px-6 md:px-12 py-12 md:py-16 space-y-10 text-[#504f4c] leading-relaxed">

        <div className="space-y-6">
          <h2
            className="text-3xl md:text-4xl font-light text-[#0d0d0d]"
            style={{ fontFamily: "'Playfair Display', 'Times New Roman', serif" }}
          >
            (1) Introduction
          </h2>
          <p>
            Testa Care, a trading name of Sigma Universal Ltd, is committed to protecting and respecting your privacy. This policy (together with Testa Care's Legal Notice and any other documents referred to in it) sets out the basis on which any personal data that you provide to Testa Care (including its subsidiaries, where applicable) will be processed.
          </p>
          <p>
            Personal data is information which, by itself or together with other information available to us, can be used to identify you as an individual ("data subject"). By processing, we mean any activity involving your personal data, including collecting, recording, storing, retrieving, using, amending, disclosing, destroying, or transferring it to other individuals or organisations.
          </p>
          <p>
            Please read this policy carefully to understand Testa Care's views and practices regarding your personal data and how it will be treated.
          </p>
        </div>

        <div className="space-y-6">
          <h2
            className="text-3xl md:text-4xl font-light text-[#0d0d0d]"
            style={{ fontFamily: "'Playfair Display', 'Times New Roman', serif" }}
          >
            (2) The types of personal data Testa Care may collect from you
          </h2>
          <p>
            Personal information is usually provided to Testa Care by the individual (service user, family member, representative, contractor, employee, or visitor). Personal information provided to other organisations may also be shared with us where appropriate (for example, by local authorities, healthcare professionals, or commissioning bodies).
          </p>
          <p>
            Testa Care may collect and process the following personal data:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Personal details:</strong> name, date of birth, email address, home address, telephone number, National Insurance number, gender, disability, sexual orientation, criminal convictions, racial or ethnic origin, religious or other beliefs</li>
            <li><strong>Family and contact details:</strong> next of kin, emergency contacts, and relationships</li>
            <li><strong>Financial information:</strong> bank and payment details</li>
            <li><strong>Safeguarding and care information:</strong> physical and mental health, care needs, risk assessments</li>
            <li><strong>Visual images and recordings:</strong> photographs and CCTV recordings</li>
            <li>Information relating to offences or alleged offences</li>
            <li>Details of services provided or received</li>
          </ul>
          <p>
            Information relating to health, safeguarding, or criminal offences is classified as <strong>special category data</strong>. We will only process such data where you have given explicit consent, where it is required for legal or safeguarding reasons, or where it has been deliberately made public by you.
          </p>
          <p>
            If you contact Testa Care, we may keep a record of that correspondence, along with details of visits to our premises or website where applicable.
          </p>
        </div>

        <div className="space-y-6">
          <h2
            className="text-3xl md:text-4xl font-light text-[#0d0d0d]"
            style={{ fontFamily: "'Playfair Display', 'Times New Roman', serif" }}
          >
            (3) The reasons why Testa Care collects personal information
          </h2>
          <p>
            We collect personal information to fulfil our legitimate business and care-related needs, including to:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>provide health, care, and support services</li>
            <li>meet safeguarding and regulatory obligations</li>
            <li>manage service users, staff, and contractors</li>
            <li>maintain accounts, records, and compliance documentation</li>
            <li>support recruitment, training, and employment</li>
            <li>communicate with service users, families, and professionals</li>
            <li>undertake marketing and service development activities</li>
          </ul>
        </div>

        <div className="space-y-6">
          <h2
            className="text-3xl md:text-4xl font-light text-[#0d0d0d]"
            style={{ fontFamily: "'Playfair Display', 'Times New Roman', serif" }}
          >
            (4) Testa Care's legal basis and purposes for using your personal information
          </h2>
          <p>
            We collect and use personal information under the following legal bases:
          </p>

          <h3 className="text-xl font-semibold text-[#2f4c74] mt-4">a) Performance of a contract</h3>
          <p>
            Where processing is necessary to fulfil contracts for services, employment, or consultancy arrangements.
          </p>

          <h3 className="text-xl font-semibold text-[#2f4c74] mt-4">b) Legitimate interests</h3>
          <p>
            Where processing is necessary for Testa Care's operational, safeguarding, and security needs, provided these do not override your rights and freedoms. This includes the use of CCTV for safety and crime prevention and maintaining communication records.
          </p>

          <h3 className="text-xl font-semibold text-[#2f4c74] mt-4">c) Legal obligations</h3>
          <p>
            Where we are required to process personal data to comply with applicable laws, regulations, and regulatory bodies.
          </p>

          <h3 className="text-xl font-semibold text-[#2f4c74] mt-4">d) Public interest</h3>
          <p>
            Where processing is necessary to protect vulnerable individuals or to cooperate with statutory agencies such as local authorities, healthcare bodies, or the police.
          </p>

          <h3 className="text-xl font-semibold text-[#2f4c74] mt-4">e) Vital interests</h3>
          <p>
            Where processing is required to protect someone's life or health, including emergency contact and medical information.
          </p>

          <h3 className="text-xl font-semibold text-[#2f4c74] mt-4">f) Consent</h3>
          <p>
            Where you have given explicit consent, including:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>use of photographs for marketing or promotional purposes</li>
            <li>receiving marketing communications</li>
          </ul>
          <p>
            You may withdraw your consent at any time using the same method by which it was provided.
          </p>
        </div>

        <div className="space-y-6">
          <h2
            className="text-3xl md:text-4xl font-light text-[#0d0d0d]"
            style={{ fontFamily: "'Playfair Display', 'Times New Roman', serif" }}
          >
            (5) Who Testa Care may share your personal information with
          </h2>
          <p>
            We may share personal information, where appropriate and lawful, with:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>family members or authorised representatives</li>
            <li>local authorities and regulatory bodies</li>
            <li>healthcare professionals and social care organisations</li>
            <li>police, courts, and safeguarding agencies</li>
            <li>contractors and service providers</li>
            <li>financial institutions and auditors</li>
          </ul>
          <p>
            All sharing is carried out in line with data protection legislation.
          </p>
        </div>

        <div className="space-y-6">
          <h2
            className="text-3xl md:text-4xl font-light text-[#0d0d0d]"
            style={{ fontFamily: "'Playfair Display', 'Times New Roman', serif" }}
          >
            (6) Transferring your personal information internationally
          </h2>
          <p>
            Personal data is normally processed within the United Kingdom. Where transfers outside the UK or EEA are required, these will be made in full compliance with UK GDPR and the Data Protection Act 2018, using appropriate safeguards.
          </p>
        </div>

        <div className="space-y-6">
          <h2
            className="text-3xl md:text-4xl font-light text-[#0d0d0d]"
            style={{ fontFamily: "'Playfair Display', 'Times New Roman', serif" }}
          >
            (7) Retention of personal information
          </h2>
          <p>
            We retain personal information only for as long as necessary for the purposes for which it was collected, or to comply with legal and regulatory requirements. Retention periods vary depending on the type of information and its use.
          </p>
          <p>
            We regularly review the data we hold and securely delete information that is no longer required.
          </p>
        </div>

        <div className="space-y-6">
          <h2
            className="text-3xl md:text-4xl font-light text-[#0d0d0d]"
            style={{ fontFamily: "'Playfair Display', 'Times New Roman', serif" }}
          >
            (8) How we keep your personal information safe
          </h2>
          <p>
            We apply appropriate technical and organisational security measures to protect personal data against loss, misuse, unauthorised access, or disclosure.
          </p>
          <p>
            Access to personal data is restricted to authorised staff who receive data protection training. Where third parties process data on our behalf, contractual safeguards are put in place.
          </p>
          <p>
            Our website may contain links to third-party websites. We are not responsible for their privacy practices and encourage you to review their policies separately.
          </p>
        </div>

        <div className="space-y-6">
          <h2
            className="text-3xl md:text-4xl font-light text-[#0d0d0d]"
            style={{ fontFamily: "'Playfair Display', 'Times New Roman', serif" }}
          >
            (9) Automated decision-making
          </h2>
          <p>
            Testa Care does not make decisions solely by automated means. Any decisions involving personal data are subject to human oversight.
          </p>
        </div>

        <div className="space-y-6">
          <h2
            className="text-3xl md:text-4xl font-light text-[#0d0d0d]"
            style={{ fontFamily: "'Playfair Display', 'Times New Roman', serif" }}
          >
            (10) Your data protection rights
          </h2>
          <p>
            You have the right to:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>request access to your personal data</li>
            <li>request correction of inaccurate or incomplete data</li>
            <li>request erasure of your data where appropriate</li>
            <li>object to or restrict processing</li>
            <li>request data portability</li>
            <li>withdraw consent at any time</li>
          </ul>
          <p>
            To exercise these rights, please contact us using the details below.
          </p>
        </div>

        <div className="space-y-6">
          <h2
            className="text-3xl md:text-4xl font-light text-[#0d0d0d]"
            style={{ fontFamily: "'Playfair Display', 'Times New Roman', serif" }}
          >
            (11) IP addresses and cookies
          </h2>
          <p>
            Our website may collect technical information such as IP address, browser type, and operating system for administration and performance purposes. Cookies may be used to improve user experience. You can manage cookie preferences through your browser settings.
          </p>
        </div>

        <div className="space-y-6">
          <h2
            className="text-3xl md:text-4xl font-light text-[#0d0d0d]"
            style={{ fontFamily: "'Playfair Display', 'Times New Roman', serif" }}
          >
            (12) Changes to this privacy policy
          </h2>
          <p>
            Any updates to this Privacy Policy will be posted on this page.
          </p>
        </div>

        <div className="space-y-6">
          <h2
            className="text-3xl md:text-4xl font-light text-[#0d0d0d]"
            style={{ fontFamily: "'Playfair Display', 'Times New Roman', serif" }}
          >
            (13) Data Protection Officer
          </h2>
          <p>
            Testa Care has appointed a Data Protection Officer to oversee compliance with this policy.
          </p>
        </div>

        <div className="space-y-6">
          <h2
            className="text-3xl md:text-4xl font-light text-[#0d0d0d]"
            style={{ fontFamily: "'Playfair Display', 'Times New Roman', serif" }}
          >
            (14) Right to complain to the Information Commissioner
          </h2>
          <p>
            If you are dissatisfied with how we handle your personal data, you have the right to complain to the Information Commissioner's Office (ICO).
          </p>
        </div>

        <div className="space-y-6">
          <h2
            className="text-3xl md:text-4xl font-light text-[#0d0d0d]"
            style={{ fontFamily: "'Playfair Display', 'Times New Roman', serif" }}
          >
            (15) Contact us
          </h2>
          <p>
            If you have any questions, concerns, or requests regarding this Privacy Policy or your personal data, please contact:
          </p>
          <div className="bg-[#fdf5ed] p-6 rounded-lg border border-[#f4e7da]">
            <p className="font-semibold text-[#0d0d0d]">Data Protection Officer</p>
            <p className="font-semibold text-[#0d0d0d] mt-2">Testa Care (Sigma Universal Ltd)</p>
            <p>Davyfield Road</p>
            <p>Blackburn</p>
            <p>Lancashire</p>
            <p>England</p>
            <p>BB1 2QY</p>
            <p className="mt-2">Email: <a href="mailto:info@testacare.co.uk" className="text-[#2f4c74] underline hover:text-[#244061]">info@testacare.co.uk</a></p>
            <p>Telephone: +44 (0)7767 054238</p>
          </div>
        </div>

        <p className="pt-6 border-t border-[#f1e3d6] text-sm tracking-[0.2em] uppercase text-[#2f4c74]">
          Updated: {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-')}
        </p>
      </div>
    </div>
  </section>
);

export default PrivacyPolicySection;
